import { useSaudeLive, useSaudeReady } from '@/features/diagnostico/hooks/useSaude';
import { descreverErro } from '@/lib/erros';
import type { RelatorioDeSaude, StatusDeSaude } from '@/types/api';

import estilos from './PaginaDeDiagnostico.module.css';

/**
 * Pagina de diagnostico da Fase 1.
 *
 * Prova que a SPA fala com a API: consome /health/live e /health/ready e
 * mostra o resultado. Sera substituida pelo Dashboard CS na Fase 6.
 */
export function PaginaDeDiagnostico() {
  const live = useSaudeLive();
  const ready = useSaudeReady();

  return (
    <main className={estilos.pagina}>
      <header className={estilos.cabecalho}>
        <h1 className={estilos.titulo}>Diagnóstico</h1>
        <p className={estilos.subtitulo}>
          Verificacao de conectividade entre a interface e a API. Fase 1 da implementacao.
        </p>
      </header>

      <div className={estilos.grade}>
        <CartaoDeSaude
          rotulo="Processo da API"
          endereco="/health/live"
          carregando={live.isPending}
          erro={live.error}
          relatorio={live.data}
          aoRecarregar={() => void live.refetch()}
        />

        <CartaoDeSaude
          rotulo="Dependencias externas"
          endereco="/health/ready"
          carregando={ready.isPending}
          erro={ready.error}
          relatorio={ready.data}
          aoRecarregar={() => void ready.refetch()}
        />
      </div>
    </main>
  );
}

interface CartaoDeSaudeProps {
  rotulo: string;
  endereco: string;
  carregando: boolean;
  erro: Error | null;
  relatorio: RelatorioDeSaude | undefined;
  aoRecarregar: () => void;
}

function CartaoDeSaude({
  rotulo,
  endereco,
  carregando,
  erro,
  relatorio,
  aoRecarregar,
}: CartaoDeSaudeProps) {
  return (
    <section className={estilos.cartao} aria-busy={carregando}>
      <div className={estilos.cartaoTopo}>
        <h2 className={estilos.cartaoTitulo}>{rotulo}</h2>
        <code className={estilos.endereco}>{endereco}</code>
      </div>

      {carregando ? <Esqueleto /> : null}

      {!carregando && erro !== null ? (
        <div className={estilos.erro} role="alert">
          <p className={estilos.erroTexto}>{descreverErro(erro)}</p>
          <button type="button" className={estilos.botao} onClick={aoRecarregar}>
            Tentar novamente
          </button>
        </div>
      ) : null}

      {!carregando && erro === null && relatorio !== undefined ? (
        <>
          <Selo status={relatorio.status} />

          <ul className={estilos.lista}>
            {relatorio.verificacoes.map((verificacao) => (
              <li key={verificacao.nome} className={estilos.item}>
                <div className={estilos.itemTopo}>
                  <span className={estilos.itemNome}>{verificacao.nome}</span>
                  <Selo status={verificacao.status} compacto />
                </div>
                {verificacao.descricao !== null ? (
                  <p className={estilos.itemDescricao}>{verificacao.descricao}</p>
                ) : null}
              </li>
            ))}
          </ul>

          <p className={estilos.duracao}>
            Respondeu em <span className={estilos.numero}>{relatorio.duracaoMs}</span> ms
          </p>
        </>
      ) : null}
    </section>
  );
}

/**
 * Estado comunicado por texto, forma e cor. Nunca por cor sozinha.
 * Decisao 01 da especificacao.
 */
function Selo({ status, compacto = false }: { status: StatusDeSaude; compacto?: boolean }) {
  const rotulos: Record<StatusDeSaude, string> = {
    Healthy: 'Saudavel',
    Degraded: 'Nao verificado',
    Unhealthy: 'Indisponivel',
  };

  const simbolos: Record<StatusDeSaude, string> = {
    Healthy: '\u25CF',
    Degraded: '\u25D0',
    Unhealthy: '\u25CB',
  };

  return (
    <span
      className={compacto ? `${estilos.selo} ${estilos.seloCompacto}` : estilos.selo}
      data-status={status}
    >
      <span aria-hidden="true">{simbolos[status]}</span>
      {rotulos[status]}
    </span>
  );
}

function Esqueleto() {
  return (
    <div className={estilos.esqueleto} aria-hidden="true">
      <span className={estilos.barra} />
      <span className={estilos.barra} />
      <span className={`${estilos.barra} ${estilos.barraCurta}`} />
    </div>
  );
}
